import { useTranslation } from 'react-i18next';
import { ActionState } from 'services/EpdClient';
import { useUpdateEpdGeneralInformation } from 'services/api/mutations';
import {
  useCountriesLookup,
  useEPDDefinitionInformation,
  useEpdOrganizationInformation,
  useEpdOrganizationInformationAddress,
} from 'services/api/queries';
import styled, { useTheme } from 'styled-components';
import { A, HyperLink } from 'styles/Styles.styled';
import { Theme } from 'styles/theme';
import { ActionFieldPanelFullWidth, CheckBoxPanel, FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';
import { CompilerCompositeStatusModel } from 'types/types';
import DatePicker from '../copy/DatePicker';
import WizardCheckbox from '../epd-wizard-inputs/WizardCheckbox';
import WizardTextInputComponent from '../epd-wizard-inputs/WizardTextInput';
import { HelpBox } from '../help-boxes';
import { usePcrV2Loading } from './hooks/usePcrV2';
import CompilerAdminDownloadIlcdEpd from './panels/compiler-admin-download-ilcd-epd/CompilerAdminDownloadIlcdEpd';
import SkeletonStep5 from './skeletons/SkeletonStep5';

const Step5: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  validationState: CompilerCompositeStatusModel | undefined;
}> = ({ epdId, epdVersionId, validationState }) => {
  const organizationInformation = useEpdOrganizationInformation(epdVersionId).data;
  const countries = useCountriesLookup().data;
  const epdOrganizationAddress = useEpdOrganizationInformationAddress(epdVersionId).data;
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const epdDefinition = useEPDDefinitionInformation(epdVersionId).data;
  const updateEpdGeneralInformation = useUpdateEpdGeneralInformation(epdVersionId);
  const isPcrV2Loading = usePcrV2Loading();
  const selectedCountryOrginal =
    countries?.find((country: any) => country.id === epdOrganizationAddress?.address.countryId) ?? null;

  const isLoading =
    organizationInformation === undefined ||
    epdDefinition === undefined ||
    isPcrV2Loading ||
    validationState === undefined ||
    countries === undefined ||
    epdOrganizationAddress === undefined;
  if (isLoading) {
    return <SkeletonStep5 />;
  }

  const versionDate = epdDefinition.versionDate ? new Date(epdDefinition.versionDate) : undefined;
  const validityDate = epdDefinition.validityDate ? new Date(epdDefinition.validityDate) : undefined;
  const firstPublishedVersionDate = epdDefinition.firstPublishedVersionDate
    ? new Date(epdDefinition.firstPublishedVersionDate)
    : undefined;

  const handleChangeProperty = (propertyName: string, newValue: any) => {
    updateEpdGeneralInformation.mutate({
      epdId,
      versionId: epdVersionId,
      propertyName,
      newValue,
    });
  };

  const handleDateChange = (propertyName: string) => (e: any) => {
    if (!e.target.value) {
      handleChangeProperty(propertyName, null);
      return;
    }
    const inputDate = new Date(e.target.value as Date);
    const utcDate = new Date(Date.UTC(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate()));
    handleChangeProperty(propertyName, utcDate.toISOString());
  };

  return (
    <FieldPanelsContainer>
      {validationState.actionsStatus.downloadIlcdEpdBundle === ActionState.Enabled && (
        <CompilerAdminDownloadIlcdEpd epdVersionId={epdVersionId} />
      )}

      <ActionFieldPanelFullWidth actionState={validationState.fieldsState.versionDate ?? ActionState.Hidden}>
        {validationState.fieldsState.firstPublishedVersionDate !== ActionState.Hidden && (
          <DatePicker
            onChange={handleDateChange('firstPublishedVersionDate')}
            dateFormat="yy-mm-dd"
            label="First version date"
            tooltip="Version date of the first published EPD version."
            initialDate={firstPublishedVersionDate}
            disabled={
              (validationState.fieldsState.firstPublishedVersionDate || ActionState.Disabled) === ActionState.Disabled
            }
            error={validationState.errors.firstPublishedVersionDate}
          />
        )}
        <DatePicker
          onChange={handleDateChange('versionDate')}
          dateFormat="yy-mm-dd"
          label="Version date"
          initialDate={versionDate}
          tooltip="The date when the current EPD version was formally approved and came into effect."
          required={validationState.errors.versionDate}
          disabled={validationState.fieldsState.versionDate === ActionState.Disabled}
          error={validationState.errors.versionDate}
        />
        <DatePicker
          onChange={handleDateChange('validityDate')}
          dateFormat="yy-mm-dd"
          label="Validity date"
          initialDate={validityDate}
          tooltip="The date until which the EPD remains valid and can be used or referenced. It is defined during verification and must match the date stated in the verification report. The validity period is normally five years starting from the version date; but can be shorter depending on the EPD type or if shorter validity is decided by the EPD Owner."
          required={validationState.errors.validityDate}
          disabled={validationState.fieldsState.validityDate !== ActionState.Enabled}
          error={validationState.errors.validityDate}
        />
      </ActionFieldPanelFullWidth>
      <ActionFieldPanelFullWidth actionState={validationState.fieldsState.confirmationBlock ?? ActionState.Hidden}>
        <FieldPanelHeader>Confirmation for publication</FieldPanelHeader>
        <HelpBox>
          <span>EPD Owner should accept the service agreement and submit the EPD for publication. </span>
        </HelpBox>
        <div>
          <ContentWrapper>
            <p>
              As an authorized person of the EPD Owner, the EPD Owner hereby requests to register and publish the EPD in the
              International EPD System, and confirms the accuracy and completeness of the attached documentation and its
              contents, and hereby undertake to observe the terms and conditions stated in the Service Agreement including
              its appendix, and the from time to time applicable General Programme Instructions, and the applicable PCR for
              the EPD included in this Agreement.
            </p>
            <p>
              The EPD Owner agrees that name and e-mail address provided above and in the submitted EPD may be stored
              electronically, may be used as contact information regarding the EPD and the services under this Agreement, and
              that the data displayed in the EPD will be published at{' '}
              <HyperLink target="_blank" rel="noopener noreferrer" href="https://www.environdec.com">
                www.environdec.com
              </HyperLink>
              .
              <br />
              EPDs using the ECO Platform logo and machine-readable EPD files will also be published on other relevant
              platforms.
            </p>
            <p>
              <strong>{t('epdWizard.wizardStep4.properties.confirmation.infoFees')}</strong>
            </p>
            <ServiceAgreementArea href={theme.serviceAgreementUrl}>
              <i className="pi pi-external-link" style={{ fontSize: '0.75rem', color: 'White', marginRight: '0.5rem' }}></i>
              <p style={{ color: 'White', margin: 0 }}>Service agreement</p>
            </ServiceAgreementArea>
          </ContentWrapper>
          <CheckBoxPanel>
            <WizardCheckbox
              inputId="serviceAgreementStatus"
              label={'I have read and accept the service agreement'}
              name={'serviceAgreementStatus'}
              value={epdDefinition.serviceAgreementStatus}
              onChanged={handleChangeProperty}
            />
          </CheckBoxPanel>
        </div>
      </ActionFieldPanelFullWidth>
      <FieldPanelFullWidth>
        <WizardTextInputComponent
          label="EPD Owner"
          name="companyName"
          disabled
          value={organizationInformation?.companyName}
        />
        <WizardTextInputComponent
          label="Contact e-mail"
          name="contactEmail"
          disabled
          value={organizationInformation.contactPersonEmail}
        />
        <WizardTextInputComponent
          label="Contact person name"
          name="contactPerson"
          disabled
          value={organizationInformation.contactPersonName}
        />
        <WizardTextInputComponent label="Country" name="country" disabled value={selectedCountryOrginal?.name} />
        <WizardTextInputComponent label="Copyright" disabled name="copyright" value={'Yes'} />
        <WizardTextInputComponent
          label="License type"
          disabled
          name="licenseType"
          value={'Free of charge for all users and uses'}
        />
      </FieldPanelFullWidth>
    </FieldPanelsContainer>
  );
};

const FieldPanelsContainer = styled.div`
  &&& > div:not([hidden]):not([style*='display: none']):first-of-type {
    margin-top: 0 !important;
  }
`;

const ContentWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.colors.elementsInputBorder};
  padding: 1rem 1.5rem 1rem 1rem;
  border-radius: 4px;
  font-size: 14px;
  width: 100%;

  p {
    margin-top: 0;
  }
`;

const ServiceAgreementArea = styled(A)`
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  width: 160px;
  height: 2.5vh;
  background-color: ${(props) => props.theme.colors.primaryOrange};
  border-radius: 2px;
  &:hover {
    background-color: ${(props) => props.theme.colors.lightOrange};
  }
`;

export default Step5;
